import React, { useState } from "react";
import { Button, Modal, Form } from 'react-bootstrap';
import { getFirestore, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper"; // Adjust path if needed

export function ReportIncident({ profile, emailAddress, theme }) {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [show, setShow] = useState(false);
    const [details, setDetails] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Local state to store resolved profile data if only an email was provided
    const [resolvedProfile, setResolvedProfile] = useState(null);

    const handleOpen = async () => {
        // If a full profile prop wasn't passed, look it up via the emailAddress prop
        if (!profile && emailAddress) {
            spinner(true);
            try {
                const q = query(collection(db, "profiles"), where("email", "==", emailAddress.toLowerCase().trim()));
                const querySnapshot = await getDocs(q);
                
                if (!querySnapshot.empty) {
                    const docSnap = querySnapshot.docs[0];
                    setResolvedProfile({ id: docSnap.id, ...docSnap.data() });
                } else {
                    // Fallback baseline if no matching profile document exists yet
                    setResolvedProfile({
                        id: "unknown_fallback",
                        name: emailAddress.split('@')[0],
                        email: emailAddress
                    });
                }
            } catch (err) {
                console.error("Error fetching profile by email:", err);
                toast.error("Failed to resolve member profile");
            } finally {
                spinner(false);
            }
        } else {
            setResolvedProfile(profile);
        }
        setShow(true);
    };

    const handleHide = () => {
        setShow(false);
        setDetails('');
    };

    const handleSave = async () => {
        if (!details.trim()) return toast.error("Please enter incident details/facts.");
        if (!resolvedProfile) return toast.error("No profile target loaded.");
        setIsSaving(true);
        
        const user = getAuth().currentUser;
        const bartenderName = user?.displayName || user?.email || "Bar Staff";
        const currentTimestamp = new Date();
        const isoString = currentTimestamp.toISOString();
        const simplifiedDate = currentTimestamp.toISOString().split('T')[0];

        try {
            const historyEntry = { 
                action: "Incident Logged via Bar Staff", 
                admin: bartenderName, 
                date: isoString 
            };

            await addDoc(collection(db, "incidentz"), {
                name: "Staff Logged Incident Report",
                details: details,
                assignedTo: resolvedProfile.name || '',
                assignedEmail: resolvedProfile.email || '',
                profileId: resolvedProfile.id,
                incidentDate: simplifiedDate,
                isPrivate: false,
                status: 'Cleared',
                createdBy: bartenderName,
                created: currentTimestamp,
                updated: currentTimestamp,
                lastModifiedBy: bartenderName,
                fileURLs: [],
                activityLog: [historyEntry]
            });

            toast.success("Incident reported to management.");
            handleHide();
        } catch (err) {
            console.error("Error logging light incident:", err);
            toast.error("Database Error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Button onClick={handleOpen} className="circle-btn centerCircle  text-warning" title="Report Incident">
                <FaExclamationTriangle size={10} />
            </Button>

            <Modal 
                show={show} onHide={handleHide} centered size="md"
                backdropClassName={isDark ? "adminBackdrop" : "docLight"}
                contentClassName={isDark ? "bg-dark text-white shadow-lg" : "bg-light text-dark shadow-lg"}
            >
                <Modal.Header closeButton closeVariant={isDark ? "white" : undefined} className="border-0 pb-0">
                    <Modal.Title className="fw-bold opacity-75">
                        Report Incident: {resolvedProfile?.name || emailAddress}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <Form.Group className="mb-3">
                        <Form.Label className="text-uppercase small text-info fw-bold opacity-75 mb-1 d-block">Facts / Details</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={5} 
                            className={isDark ? "glass-input text-white" : "form-control"}
                            placeholder="Provide a detailed description of what occurred..."
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="secondary" onClick={handleHide}>Cancel</Button>
                    <Button variant="info" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "Submitting..." : "Submit Report"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ReportIncident;